import { useCallback, useEffect, useRef, useState } from "react";
import {
  calculateScrollOffset,
  findScrollContainer,
} from "@/partials/document-renderer/utils/scrollUtils";
import { CONSOLIDATION_CHANGE_GROUP_CLASS } from "@/partials/document-renderer/utils/consolidationChangeGrouping";

export const CONSOLIDATION_CHANGE_FOCUSED_CLASS = "consolidation-change-focused";

/** The visual/interactive target for a changed section: its group wrapper if any. */
const sectionTargetFor = (element: HTMLElement): HTMLElement =>
  element.closest<HTMLElement>(`.${CONSOLIDATION_CHANGE_GROUP_CLASS}`) ?? element;

export type ConsolidationChangeContainer = {
  id: string;
  type: string;
};

export type ChangedContainerNavigation = {
  /** One representative element per navigable changed section, in document order. */
  stops: HTMLElement[];
  /**
   * Maps every changed container id (including ones collapsed into a section) to
   * the index of the section it belongs to, so a click on any member focuses the
   * right stop.
   */
  stopIndexByContainerId: Map<string, number>;
};

/**
 * Groups changed containers into amendment-point sections, in document order.
 * Consecutive changed containers that share the same group key form one section
 * (e.g. a group heading inserted together with its paragraphs).
 *
 * Grouping is limited to containers adjacent in document order: an unchanged
 * container between two changed ones breaks the run, so scattered changes from
 * the same point (e.g. an "Overalt i loven" global replace) remain separate.
 *
 * Adjacency must match `wrapGroupedChangedContainersHtml` exactly — same parent
 * and direct element siblings — because that wrapper is what makes a run one
 * pressable/highlighted section. If this grouped a run the wrapper does not
 * (e.g. a non-container element sits between two changed containers, or they
 * live in different parents), navigation would collapse them while only the
 * first member got the highlight/click classes, leaving later members
 * unclickable.
 */
export const groupChangedContainersInDocumentOrder = (
  docRoot: HTMLElement,
  changedContainerIds: ReadonlySet<string>,
  groupKeys?: ReadonlyMap<string, string>
): HTMLElement[][] => {
  const groups: HTMLElement[][] = [];
  let current: HTMLElement[] = [];
  let previousChangedKey: string | null = null;
  let previousChangedEl: HTMLElement | null = null;

  const flush = () => {
    if (current.length > 0) {
      groups.push(current);
      current = [];
    }
  };

  for (const el of Array.from(
    docRoot.querySelectorAll<HTMLElement>('[data-container-type][id]')
  )) {
    if (!changedContainerIds.has(el.id)) {
      flush();
      previousChangedKey = null;
      previousChangedEl = null;
      continue;
    }

    const key = groupKeys?.get(el.id) ?? null;
    const continuesRun =
      key !== null &&
      previousChangedKey !== null &&
      key === previousChangedKey &&
      previousChangedEl !== null &&
      previousChangedEl.parentNode === el.parentNode &&
      previousChangedEl.nextElementSibling === el;

    if (continuesRun) {
      current.push(el);
    } else {
      flush();
      current = [el];
    }
    previousChangedKey = key;
    previousChangedEl = el;
  }
  flush();

  return groups;
};

/**
 * Builds the navigable changed sections in document order. Each section is
 * represented by its first container; every container (including collapsed
 * members) maps to its section index.
 */
export const buildChangedContainerNavigation = (
  docRoot: HTMLElement,
  changedContainerIds: ReadonlySet<string>,
  groupKeys?: ReadonlyMap<string, string>
): ChangedContainerNavigation => {
  const groups = groupChangedContainersInDocumentOrder(
    docRoot,
    changedContainerIds,
    groupKeys
  );
  const stops: HTMLElement[] = [];
  const stopIndexByContainerId = new Map<string, number>();

  groups.forEach((members, index) => {
    stops.push(members[0]);
    for (const member of members) {
      stopIndexByContainerId.set(member.id, index);
    }
  });

  return { stops, stopIndexByContainerId };
};

export const collectChangedContainersInDocumentOrder = (
  docRoot: HTMLElement,
  changedContainerIds: ReadonlySet<string>,
  groupKeys?: ReadonlyMap<string, string>
): HTMLElement[] =>
  buildChangedContainerNavigation(docRoot, changedContainerIds, groupKeys).stops;

/** Stable key for DOM rescan dedup — full id set (not just count) plus node count. */
export const buildConsolidationChangeScanSignature = (
  changedContainerIds: ReadonlySet<string>,
  domContainerCount: number
): string =>
  `${[...changedContainerIds].sort().join("\0")}:${domContainerCount}`;

const toContainerMeta = (element: HTMLElement): ConsolidationChangeContainer => ({
  id: element.id,
  type: element.getAttribute("data-container-type") ?? "",
});

export const scrollChangedContainerIntoView = (
  docRoot: HTMLElement,
  element: HTMLElement
) => {
  const scroller = findScrollContainer(docRoot);
  if (!scroller) {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const containerRect = scroller.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollOffset = calculateScrollOffset(scroller);
      const elementOffsetFromContainerTop =
        elementRect.top - containerRect.top;
      const elementPositionInContent =
        scroller.scrollTop + elementOffsetFromContainerTop;
      scroller.scrollTop = Math.max(0, elementPositionInContent - scrollOffset);
    });
  });
};

export type UseConsolidationChangeNavigationOptions = {
  docRootRef: React.RefObject<HTMLElement | null>;
  changedContainerIds: ReadonlySet<string>;
  changedContainerGroupKeys?: ReadonlyMap<string, string>;
  enabled: boolean;
  contentReady: boolean;
  activeContainerId?: string | null;
  onNavigate?: (container: ConsolidationChangeContainer) => void;
};

export type UseConsolidationChangeNavigationReturn = {
  currentIndex: number;
  totalChanges: number;
  focusContainerId: string | null;
  goToNext: () => void;
  goToPrevious: () => void;
};

export const useConsolidationChangeNavigation = ({
  docRootRef,
  changedContainerIds,
  changedContainerGroupKeys,
  enabled,
  contentReady,
  activeContainerId,
  onNavigate,
}: UseConsolidationChangeNavigationOptions): UseConsolidationChangeNavigationReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalChanges, setTotalChanges] = useState(0);
  const [focusContainerId, setFocusContainerId] = useState<string | null>(null);

  const containersRef = useRef<HTMLElement[]>([]);
  const stopIndexByContainerIdRef = useRef<Map<string, number>>(new Map());
  const currentIndexRef = useRef(0);
  const onNavigateRef = useRef(onNavigate);
  const lastScanSignatureRef = useRef("");

  useEffect(() => {
    onNavigateRef.current = onNavigate;
  }, [onNavigate]);

  const clearFocus = useCallback(() => {
    docRootRef.current
      ?.querySelectorAll(`.${CONSOLIDATION_CHANGE_FOCUSED_CLASS}`)
      .forEach((el) => el.classList.remove(CONSOLIDATION_CHANGE_FOCUSED_CLASS));
    setFocusContainerId(null);
  }, [docRootRef]);

  const focusAtIndex = useCallback(
    (index: number, notify = true) => {
      const containers = containersRef.current;
      if (containers.length === 0) return;

      const target = containers[index];
      if (!target) return;

      const sectionTarget = sectionTargetFor(target);
      clearFocus();
      sectionTarget.classList.add(CONSOLIDATION_CHANGE_FOCUSED_CLASS);
      setFocusContainerId(target.id);
      setCurrentIndex(index);
      currentIndexRef.current = index;

      const docRoot = docRootRef.current;
      if (docRoot) {
        scrollChangedContainerIntoView(docRoot, sectionTarget);
      }

      if (notify) {
        onNavigateRef.current?.(toContainerMeta(target));
      }
    },
    [clearFocus, docRootRef]
  );

  const scanContainers = useCallback(() => {
    const docRoot = docRootRef.current;
    if (!docRoot || !enabled) {
      containersRef.current = [];
      stopIndexByContainerIdRef.current = new Map();
      lastScanSignatureRef.current = "";
      clearFocus();
      setTotalChanges(0);
      setCurrentIndex(0);
      currentIndexRef.current = 0;
      return;
    }

    const domContainerCount = docRoot.querySelectorAll('[data-container-type][id]').length;
    const { stops: containers, stopIndexByContainerId } =
      buildChangedContainerNavigation(
        docRoot,
        changedContainerIds,
        changedContainerGroupKeys
      );
    containersRef.current = containers;
    stopIndexByContainerIdRef.current = stopIndexByContainerId;
    const signature = buildConsolidationChangeScanSignature(
      changedContainerIds,
      domContainerCount
    );
    // Keep rescanning via MutationObserver until stops exist or ids are empty.
    if (containers.length > 0 || changedContainerIds.size === 0) {
      lastScanSignatureRef.current = signature;
    }
    setTotalChanges(containers.length);

    if (containers.length === 0) {
      clearFocus();
      setCurrentIndex(0);
      currentIndexRef.current = 0;
      return;
    }

    const activeIndex = activeContainerId
      ? stopIndexByContainerId.get(activeContainerId) ?? -1
      : -1;
    const nextIndex = activeIndex >= 0 ? activeIndex : currentIndexRef.current;
    const clampedIndex = Math.min(
      Math.max(nextIndex, 0),
      containers.length - 1
    );

    currentIndexRef.current = clampedIndex;
    setCurrentIndex(clampedIndex);

    if (!activeContainerId) {
      clearFocus();
      return;
    }

    const activeEl = containers[clampedIndex];
    clearFocus();
    if (activeEl) {
      sectionTargetFor(activeEl).classList.add(CONSOLIDATION_CHANGE_FOCUSED_CLASS);
      setFocusContainerId(activeEl.id);
    }
  }, [
    activeContainerId,
    changedContainerIds,
    changedContainerGroupKeys,
    clearFocus,
    docRootRef,
    enabled,
  ]);

  useEffect(() => {
    if (!enabled || !contentReady) {
      containersRef.current = [];
      lastScanSignatureRef.current = "";
      clearFocus();
      setTotalChanges(0);
      setCurrentIndex(0);
      currentIndexRef.current = 0;
      return;
    }

    lastScanSignatureRef.current = "";

    requestAnimationFrame(() => {
      requestAnimationFrame(scanContainers);
    });
  }, [
    changedContainerIds,
    clearFocus,
    contentReady,
    enabled,
    scanContainers,
  ]);

  // The consolidation HTML is rendered through `useDeferredValue` by the caller,
  // so changed-container ids can commit before the DOM updates. Observe the doc
  // root and re-scan when container nodes actually appear (signature-guarded so
  // focus/outline class toggles during navigation do not loop).
  useEffect(() => {
    const root = docRootRef.current;
    if (!root || !enabled || !contentReady) return;

    let frame = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const currentRoot = docRootRef.current;
        if (!currentRoot) return;
        const domContainerCount = currentRoot.querySelectorAll(
          '[data-container-type][id]'
        ).length;
        const signature = buildConsolidationChangeScanSignature(
          changedContainerIds,
          domContainerCount
        );
        if (signature === lastScanSignatureRef.current) return;
        scanContainers();
      });
    });

    observer.observe(root, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [changedContainerIds, contentReady, docRootRef, enabled, scanContainers]);

  useEffect(() => {
    if (!enabled || !contentReady || !activeContainerId) return;

    const containers = containersRef.current;
    const activeIndex =
      stopIndexByContainerIdRef.current.get(activeContainerId) ?? -1;
    if (activeIndex < 0 || activeIndex === currentIndexRef.current) return;

    currentIndexRef.current = activeIndex;
    setCurrentIndex(activeIndex);
    clearFocus();
    const activeEl = containers[activeIndex];
    if (activeEl) {
      sectionTargetFor(activeEl).classList.add(CONSOLIDATION_CHANGE_FOCUSED_CLASS);
      setFocusContainerId(activeEl.id);
    }
  }, [activeContainerId, clearFocus, contentReady, enabled]);

  const goToNext = useCallback(() => {
    const total = containersRef.current.length;
    if (total === 0) return;
    const nextIndex = (currentIndexRef.current + 1) % total;
    focusAtIndex(nextIndex);
  }, [focusAtIndex]);

  const goToPrevious = useCallback(() => {
    const total = containersRef.current.length;
    if (total === 0) return;
    const prevIndex = (currentIndexRef.current - 1 + total) % total;
    focusAtIndex(prevIndex);
  }, [focusAtIndex]);

  return {
    currentIndex,
    totalChanges,
    focusContainerId,
    goToNext,
    goToPrevious,
  };
};
